/**
 * GuidesPremium — Grille asymétrique magazine de la page d'accueil.
 *
 * Extrait de `pages/Home.jsx` (juin 2026) pour réduire la taille du fichier
 * principal (~273 lignes).
 *
 * Layout :
 *  • 1 HERO éditorial grand (gauche desktop, top mobile)
 *  • 1 carte wide secondaire (haut droite desktop, top mobile)
 *  • 4 cartes carrées (grille 2×2 desktop, 2×2 mobile)
 *
 * Mix images thématiques + fallback éditorial coloré quand l'image
 * Unsplash a été retirée (cohérence visuelle garantie).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Star, GraduationCap } from 'lucide-react';

const GUIDES = [
  {
    slot: 'hero',
    kicker: 'Été 2026 · Saison à haut risque',
    title: 'Un motard sur deux tombe l’été.',
    meta: '6 min · Bilan ONISR mai → septembre',
    to: '/apprendre/conducteur/ete-motard-pic-mortalite-mai-septembre',
    image:
      'https://images.unsplash.com/photo-1449426468159-d96dbf08f19f?w=1600&q=85&auto=format&fit=crop',
    accent: '#cf2031',
    tag: 'À LA UNE',
  },
  {
    slot: 'wide',
    kicker: 'Maîtriser le permis moto',
    title: 'Passer le permis A1 ou A2',
    meta: '7 min · Le guide complet 2026',
    to: '/magazine/passer-le-permis-a1-a2',
    image:
      'https://images.unsplash.com/photo-1558981359-219d6364c9c8?w=1200&q=85&auto=format&fit=crop',
    accent: '#1d4ed8',
    tag: 'NOUVEAU',
  },
  {
    slot: 'square',
    kicker: 'Programme officiel · 9 thèmes',
    title: 'Apprendre par thème',
    meta: 'Magazine pédagogique · 9 dossiers ETM',
    to: '/revision',
    accent: '#7c3aed',
    gradientFrom: '#2d1b4e',
    gradientTo: '#7c3aed',
    icon: GraduationCap,
  },
  {
    slot: 'square',
    kicker: 'Comprendre les trajectoires',
    title: 'La courbe parfaite',
    meta: '4 min · Sécurité virage',
    to: '/magazine/trajectoire-securite-virage',
    image: '/images/articles/route-rurale-sinueuse.jpg?v=20260514',
    accent: '#0891b2',
  },
  {
    slot: 'square',
    kicker: "L'équipement qui sauve",
    title: 'Casque, gants, dorsale',
    meta: '5 min · 12 points de contrôle',
    to: '/magazine/equipement-complet-motard-2026',
    image:
      'https://images.unsplash.com/photo-1544148024-c3759a58ba99?w=900&q=85&auto=format&fit=crop',
    accent: '#f59e0b',
  },
  {
    slot: 'square',
    kicker: 'Préparer le BSR',
    title: 'AM / ASSR2 en 3ᵉ',
    meta: '7 min · Scolarité',
    to: '/magazine/assr2-preparer-3eme',
    image:
      'https://images.unsplash.com/photo-1427504494785-3a9ca7044f45?w=900&q=85&auto=format&fit=crop',
    accent: '#16a34a',
  },
];

export default function GuidesPremium() {
  const navigate = useNavigate();

  // Fonction qui rend une carte (image OU fallback éditorial)
  const renderCard = (g, slotClasses, isHero = false) => (
    <button
      key={g.title}
      type="button"
      onClick={() => navigate(g.to)}
      data-testid={`guide-card-${g.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
      className={`group relative overflow-hidden rounded-xl bg-card transition-all duration-300 hover:scale-[1.015] active:scale-[0.99] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] ${slotClasses}`}
      style={{ boxShadow: '0 8px 24px -16px rgba(0,0,0,0.45)' }}
    >
      {g.image ? (
        <>
          <img
            src={g.image}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            style={{ filter: 'saturate(0.95) contrast(1.04)' }}
          />
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background: isHero
                ? 'linear-gradient(180deg, rgba(15,12,8,0.10) 0%, rgba(15,12,8,0.30) 50%, rgba(15,12,8,0.96) 100%)'
                : 'linear-gradient(180deg, rgba(15,12,8,0) 35%, rgba(15,12,8,0.92) 100%)',
            }}
          />
        </>
      ) : (
        <>
          <div
            className="absolute inset-0 transition-transform duration-700 group-hover:scale-105"
            style={{
              background: `linear-gradient(135deg, ${g.gradientFrom} 0%, ${g.gradientTo || g.accent} 100%)`,
            }}
          />
          <div
            className="absolute inset-0 opacity-[0.08] pointer-events-none"
            style={{
              backgroundImage:
                'repeating-linear-gradient(45deg, transparent 0 8px, rgba(255,255,255,0.5) 8px 9px)',
            }}
          />
          {g.icon && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <g.icon
                className={`text-white/15 transition-transform duration-700 group-hover:scale-110 ${
                  isHero ? 'w-48 h-48' : 'w-24 h-24'
                }`}
                strokeWidth={1.2}
                aria-hidden="true"
              />
            </div>
          )}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                'linear-gradient(180deg, rgba(15,12,8,0) 50%, rgba(15,12,8,0.85) 100%)',
            }}
          />
        </>
      )}

      {/* Tag en haut à gauche — variant selon le type */}
      {g.tag && (
        <span
          className="absolute top-3 left-3 inline-flex items-center px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-[0.14em] text-white backdrop-blur-md shadow-lg"
          style={{ background: `${g.accent}E6` }}
        >
          {g.tag === 'NOUVEAU' ? (
            <span
              className="w-1.5 h-1.5 rounded-full bg-white mr-1.5 animate-pulse"
              aria-hidden="true"
            />
          ) : (
            <Star className="w-2.5 h-2.5 fill-current mr-1" />
          )}
          {g.tag}
        </span>
      )}

      {/* Filet d'accent en haut */}
      <span
        className="absolute top-0 inset-x-0 h-[3px]"
        style={{ background: g.accent }}
      />

      {/* Bloc texte en bas */}
      <div className={`absolute inset-x-0 bottom-0 ${isHero ? 'p-5 sm:p-7 lg:p-9' : 'p-4 sm:p-5'} text-left`}>
        {g.kicker && (
          <span
            className={`block ${isHero ? 'text-[10px] sm:text-[11px]' : 'text-[9px] sm:text-[10px]'} font-black uppercase tracking-[0.18em] mb-1.5 sm:mb-2`}
            style={{ color: g.accent }}
          >
            {g.kicker}
          </span>
        )}
        <h3
          className={`font-extrabold tracking-tight text-white leading-[1.05] ${
            isHero
              ? 'text-2xl sm:text-4xl lg:text-5xl'
              : 'text-base sm:text-xl'
          }`}
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {g.title}
        </h3>
        <div className={`flex items-center justify-between ${isHero ? 'mt-3 sm:mt-4' : 'mt-2'}`}>
          <span className={`${isHero ? 'text-xs sm:text-sm' : 'text-[10px] sm:text-[11px]'} text-white/75 font-medium truncate`}>
            {g.meta}
          </span>
          <span
            className={`inline-flex items-center justify-center rounded-full bg-white/15 backdrop-blur-sm border border-white/20 group-hover:bg-[hsl(var(--primary))] group-hover:border-[hsl(var(--primary))] transition-colors flex-shrink-0 ml-2 ${
              isHero ? 'w-9 h-9' : 'w-7 h-7'
            }`}
            aria-hidden="true"
          >
            <ArrowRight className={isHero ? 'w-4 h-4 text-white' : 'w-3.5 h-3.5 text-white'} />
          </span>
        </div>
      </div>
    </button>
  );

  return (
    <section
      className="relative"
      data-testid="home-guides-premium"
      aria-labelledby="guides-title"
      data-reveal=""
    >
      {/* Header section */}
      <div className="flex items-end justify-between mb-4 sm:mb-5">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--primary))]" />
            <span className="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.18em] text-[hsl(var(--primary))]">
              Magazine
            </span>
          </div>
          <h2
            id="guides-title"
            className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-foreground"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            Guides premium
          </h2>
        </div>
        <button
          type="button"
          onClick={() => navigate('/magazine')}
          className="text-[11px] sm:text-xs font-bold text-foreground/70 hover:text-[hsl(var(--primary))] transition-colors inline-flex items-center gap-1 hide-in-oral"
          data-testid="guides-see-all"
        >
          Tous les guides <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Grille asymétrique magazine */}
      <div className="grid grid-cols-2 lg:grid-cols-12 gap-3 sm:gap-4 lg:auto-rows-[220px]">
        {/* HERO — col-span-2 mobile, col-span-7 row-span-3 desktop */}
        {renderCard(
          GUIDES[0],
          'col-span-2 lg:col-span-7 lg:row-span-3 aspect-[5/4] lg:aspect-auto',
          true
        )}

        {/* WIDE — col-span-2 mobile (full), col-span-5 desktop */}
        {renderCard(
          GUIDES[1],
          'col-span-2 lg:col-span-5 lg:row-span-1 aspect-[16/9] lg:aspect-auto'
        )}

        {/* 4 SQUARES — col-span-1 mobile (2×2), col-span variable desktop (2×2) */}
        {renderCard(
          GUIDES[2],
          'col-span-1 lg:col-span-2 lg:col-start-8 lg:row-span-1 lg:row-start-2 aspect-square lg:aspect-auto'
        )}
        {renderCard(
          GUIDES[3],
          'col-span-1 lg:col-span-3 lg:col-start-10 lg:row-span-1 lg:row-start-2 aspect-square lg:aspect-auto'
        )}
        {renderCard(
          GUIDES[4],
          'col-span-1 lg:col-span-2 lg:col-start-8 lg:row-span-1 lg:row-start-3 aspect-square lg:aspect-auto'
        )}
        {renderCard(
          GUIDES[5],
          'col-span-1 lg:col-span-3 lg:col-start-10 lg:row-span-1 lg:row-start-3 aspect-square lg:aspect-auto'
        )}
      </div>
    </section>
  );
}
